import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Users, Package, Mail, ShieldCheck, Loader2, MessageSquare, Zap, DollarSign,
  TrendingUp, Download, Settings, ClipboardList, UserMinus, Bell, HardDrive, Send, LayoutDashboard
} from 'lucide-react';
import { StatCard, MiniStat } from './admin/shared';
import { AdminNav } from './admin/AdminNav';

// Tab components
import { VerificationsTab } from './admin/VerificationsTab';
import { StatsTab } from './admin/StatsTab';
import { UsersTab } from './admin/UsersTab';
import { ListingsTab } from './admin/ListingsTab';
import { BoostsTab } from './admin/BoostsTab';
import { ContactsTab } from './admin/ContactsTab';
import { DeletedAccountsTab } from './admin/DeletedAccountsTab';
import { MessagesTab } from './admin/MessagesTab';
import { NotificationsTab } from './admin/NotificationsTab';
import { LogsTab } from './admin/LogsTab';
import { SettingsTab } from './admin/SettingsTab';
import { BackupTab } from './admin/BackupTab';
import { SequencesTab } from './admin/SequencesTab';
import { NewsletterTab } from './admin/NewsletterTab';
import { PilotageTab } from './admin/PilotageTab';
import { BlogTab } from './admin/BlogTab';

// Modal components
import { UserDetailModal } from './admin/UserDetailModal';
import { EmailModal } from './admin/EmailModal';
import { EditListingModal } from './admin/EditListingModal';

export default function AdminPage() {
  const { user } = useAuth();

  // Core data
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [growthData, setGrowthData] = useState(null);
  const [boostDetails, setBoostDetails] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [settings, setSettings] = useState(null);
  const [settingsChanged, setSettingsChanged] = useState(false);

  // Users tab state
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [userPages, setUserPages] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState(new Set());

  // Listings tab state
  const [listingSearch, setListingSearch] = useState('');
  const [listingStatusFilter, setListingStatusFilter] = useState('');
  const [selectedListings, setSelectedListings] = useState(new Set());

  // Charts state
  const [statsPeriod, setStatsPeriod] = useState('weekly');

  // Logs
  const [logs, setLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logPages, setLogPages] = useState(1);

  // Modals
  const [userDetail, setUserDetail] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [editListing, setEditListing] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Active tab state
  const [activeTab, setActiveTab] = useState('pilotage');

  // Deleted badge tracking
  const [seenDeletedCount, setSeenDeletedCount] = useState(() => parseInt(localStorage.getItem('admin_seen_deleted') || '0'));

  // Sales stats
  const [salesStats, setSalesStats] = useState(null);

  // Processing state
  const [processingId, setProcessingId] = useState(null);

  // ─── Data Loading ───
  const loadData = async () => {
    try {
      const [s, l, c, v] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/listings'),
        api.get('/admin/contacts'),
        api.get('/admin/verifications'),
      ]);
      setStats(s.data);
      setListings(l.data.listings);
      setContacts(c.data);
      setVerifications(v.data.verifications);
      api.get('/admin/conversations').then(r => setConversations(r.data.conversations)).catch(() => {});
      api.get('/admin/deleted-accounts').then(r => setDeletedAccounts(r.data.deleted_accounts)).catch(() => {});
      api.get('/admin/growth?days=365&weeks=52&months=12').then(r => setGrowthData(r.data)).catch(() => {});
      api.get('/admin/boosts').then(r => setBoostDetails(r.data.boosts)).catch(() => {});
      api.get('/admin/settings').then(r => { setSettings(r.data); setSettingsChanged(false); }).catch(() => {});
      api.get('/admin/notifications').then(r => { setNotifications(r.data.notifications); setNotifUnread(r.data.unread_count); }).catch(() => {});
      api.get('/admin/sales-stats').then(r => setSalesStats(r.data)).catch(() => {});
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', userPage);
      params.set('limit', 20);
      if (userSearch) params.set('search', userSearch);
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.users);
      setUserTotal(data.total);
      setUserPages(data.pages);
    } catch {}
  };

  const loadLogs = async () => {
    try {
      const { data } = await api.get(`/admin/logs?page=${logPage}&limit=30`);
      setLogs(data.logs);
      setLogPages(data.pages);
    } catch {}
  };

  const loadListings = async () => {
    try {
      const params = new URLSearchParams();
      if (listingStatusFilter) params.set('status', listingStatusFilter);
      if (listingSearch) params.set('search', listingSearch);
      const { data } = await api.get(`/admin/listings?${params.toString()}`);
      setListings(data.listings);
    } catch {}
  };

  useEffect(() => { if (user?.role === 'admin') loadData(); }, [user]);
  useEffect(() => { if (user?.role === 'admin') loadUsers(); }, [user, userPage, userSearch]);
  useEffect(() => { setUserPage(1); }, [userSearch]);
  useEffect(() => { if (user?.role === 'admin') loadListings(); }, [listingStatusFilter, listingSearch]);
  useEffect(() => { if (user?.role === 'admin') loadLogs(); }, [logPage]);

  // ─── Actions ───
  const handleVerificationAction = async (userId, action, comment) => {
    setProcessingId(userId);
    try {
      await api.put(`/admin/verifications/${userId}`, { action, admin_comment: comment });
      const newStatus = action === 'approved' ? 'verified' : (action === 'rejected' ? 'rejected' : 'pending');
      setVerifications(prev => prev.map(v => v.id === userId ? { ...v, verification_status: newStatus } : v));
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, verification_status: newStatus } : u));
      toast.success({ approved: 'Entreprise verifiee', rejected: 'Verification refusee', pending: 'Remis en attente' }[action]);
    } catch { toast.error('Erreur'); }
    finally { setProcessingId(null); }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { is_active: !currentStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
      toast.success(currentStatus ? 'Utilisateur suspendu' : 'Utilisateur reactive');
    } catch { toast.error('Erreur'); }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilisateur supprime');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const resetPassword = async (userId, pw) => {
    if (!pw || pw.length < 6) { toast.error('Min. 6 caracteres'); return; }
    try {
      await api.put(`/admin/users/${userId}/reset-password`, { new_password: pw });
      toast.success('Mot de passe reinitialise');
    } catch { toast.error('Erreur'); }
  };

  const updateListingStatus = async (listingId, newStatus) => {
    try {
      await api.put(`/admin/listings/${listingId}/status`, { status: newStatus });
      setListings(prev => prev.map(l => l.id === listingId ? { ...l, status: newStatus } : l));
      toast.success('Statut mis a jour');
    } catch { toast.error('Erreur'); }
  };

  const deleteListing = async (listingId) => {
    try {
      await api.delete(`/admin/listings/${listingId}`);
      setListings(prev => prev.filter(l => l.id !== listingId));
      toast.success('Annonce supprimee');
    } catch { toast.error('Erreur'); }
  };

  const viewUserDetail = async (userId) => {
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setUserDetail(data);
    } catch { toast.error('Erreur'); }
  };

  const toggleSelectUser = (id) => setSelectedUsers(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectListing = (id) => setSelectedListings(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllUsers = () => setSelectedUsers(prev => prev.size === users.length ? new Set() : new Set(users.map(u => u.id)));
  const selectAllListings = () => setSelectedListings(prev => prev.size === listings.length ? new Set() : new Set(listings.map(l => l.id)));

  const bulkUserAction = async (action) => {
    if (selectedUsers.size === 0) return;
    try {
      const { data } = await api.post('/admin/bulk/users', { user_ids: [...selectedUsers], action });
      toast.success(data.message);
      setSelectedUsers(new Set());
      loadUsers();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const bulkListingAction = async (action) => {
    if (selectedListings.size === 0) return;
    try {
      const { data } = await api.post('/admin/bulk/listings', { listing_ids: [...selectedListings], action });
      toast.success(data.message);
      setSelectedListings(new Set());
      loadListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const saveSettings = async () => {
    try {
      await api.put('/admin/settings', settings);
      toast.success('Parametres sauvegardes');
      setSettingsChanged(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const exportCSV = async (type) => {
    try {
      const response = await api.get(`/admin/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `destockpro_${type}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export telecharge');
    } catch { toast.error('Erreur export'); }
  };

  if (user?.role !== 'admin') return <div className="text-center py-20"><p className="text-neutral-500 dark:text-neutral-400">Acces reserve aux administrateurs</p></div>;
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>;

  return (
    <div className="site-container py-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">Administration</h1>
        <div className="hidden sm:flex gap-2">
          <Button size="sm" variant="outline" onClick={() => exportCSV('users')} data-testid="export-users"><Download className="w-4 h-4 mr-1" /> Utilisateurs</Button>
          <Button size="sm" variant="outline" onClick={() => exportCSV('listings')} data-testid="export-listings"><Download className="w-4 h-4 mr-1" /> Annonces</Button>
          <Button size="sm" variant="outline" onClick={() => exportCSV('boosts')} data-testid="export-boosts"><Download className="w-4 h-4 mr-1" /> Boosts</Button>
        </div>
        <div className="sm:hidden">
          <Button size="sm" variant="outline" onClick={() => exportCSV('users')} data-testid="export-users-mobile"><Download className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="mb-8 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="admin-stats">
            <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} sub={`+${stats.new_users_week} cette semaine`} color="blue" />
            <StatCard icon={Package} label="Annonces actives" value={stats.active_listings} sub={`+${stats.new_listings_week} cette semaine`} color="orange" />
            <StatCard icon={DollarSign} label="Revenus boosts" value={`${(stats.boost_revenue / 100).toFixed(0)}€`} sub={`${stats.boost_count} boosts vendus`} color="green" />
            <StatCard icon={Zap} label="Boosts actifs" value={stats.active_boosts} color="purple" />
            <StatCard icon={ShieldCheck} label="Verif. en attente" value={stats.pending_verifications || 0} color="amber" highlight={stats.pending_verifications > 0} />
            <StatCard icon={MessageSquare} label="Messages" value={stats.total_messages} sub={`${stats.messages_today} aujourd'hui`} color="blue" />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
            <MiniStat label="Inscriptions auj." value={stats.new_users_today} />
            <MiniStat label="Annonces auj." value={stats.new_listings_today} />
            <MiniStat label="Suspendues" value={stats.suspended_listings} warn />
            <MiniStat label="Verifiees" value={stats.verified_companies} />
            <MiniStat label="Contacts" value={stats.total_contacts} />
            <MiniStat label="Supprimes" value={stats.deleted_accounts || 0} warn={stats.deleted_accounts > 0} />
          </div>
        </div>
      )}

      {/* Navigation */}
      <AdminNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        badges={{
          verifications: stats?.pending_verifications || 0,
          notifications: notifUnread,
          deleted: deletedAccounts.length > seenDeletedCount ? deletedAccounts.length - seenDeletedCount : 0,
        }}
        onTabClick={(key) => {
          if (key === 'deleted') { setSeenDeletedCount(deletedAccounts.length); localStorage.setItem('admin_seen_deleted', String(deletedAccounts.length)); }
          if (key === 'logs') loadLogs();
        }}
      />

      {/* Tab Content */}
      <div>
        {activeTab === 'pilotage' && <PilotageTab setEmailModal={setEmailModal} setActiveTab={setActiveTab} />}
        {activeTab === 'verifications' && <VerificationsTab verifications={verifications} onAction={handleVerificationAction} processingId={processingId} />}
        {activeTab === 'stats' && <StatsTab growthData={growthData} statsPeriod={statsPeriod} setStatsPeriod={setStatsPeriod} salesStats={salesStats} />}
        {activeTab === 'users' && (
          <UsersTab
            users={users} userSearch={userSearch} setUserSearch={setUserSearch}
            userPage={userPage} setUserPage={setUserPage} userPages={userPages} userTotal={userTotal}
            selectedUsers={selectedUsers} toggleSelectUser={toggleSelectUser} selectAllUsers={selectAllUsers} bulkUserAction={bulkUserAction}
            toggleUserStatus={toggleUserStatus} deleteUser={deleteUser} resetPassword={resetPassword}
            viewUserDetail={viewUserDetail} setEmailModal={setEmailModal}
          />
        )}
        {activeTab === 'listings' && (
          <ListingsTab
            listings={listings} listingSearch={listingSearch} setListingSearch={setListingSearch}
            listingStatusFilter={listingStatusFilter} setListingStatusFilter={setListingStatusFilter}
            selectedListings={selectedListings} toggleSelectListing={toggleSelectListing} selectAllListings={selectAllListings} bulkListingAction={bulkListingAction}
            updateListingStatus={updateListingStatus} deleteListing={deleteListing}
            setEditListing={setEditListing} setEditForm={setEditForm}
          />
        )}
        {activeTab === 'boosts' && <BoostsTab boostDetails={boostDetails} />}
        {activeTab === 'contacts' && <ContactsTab contacts={contacts} />}
        {activeTab === 'deleted' && <DeletedAccountsTab deletedAccounts={deletedAccounts} />}
        {activeTab === 'messages' && <MessagesTab conversations={conversations} onRefresh={() => api.get('/admin/conversations').then(r => setConversations(r.data.conversations)).catch(() => {})} />}
        {activeTab === 'notifications' && <NotificationsTab notifications={notifications} notifUnread={notifUnread} setNotifications={setNotifications} setNotifUnread={setNotifUnread} />}
        {activeTab === 'logs' && <LogsTab logs={logs} logPage={logPage} logPages={logPages} setLogPage={setLogPage} />}
        {activeTab === 'settings' && <SettingsTab settings={settings} settingsChanged={settingsChanged} setSettings={setSettings} setSettingsChanged={setSettingsChanged} saveSettings={saveSettings} />}
        {activeTab === 'backup' && <BackupTab />}
        {activeTab === 'sequences' && <SequencesTab />}
        {activeTab === 'newsletter' && <NewsletterTab />}
        {activeTab === 'blog' && <BlogTab />}
      </div>

      {/* Modals */}
      {userDetail && <UserDetailModal userDetail={userDetail} onClose={() => setUserDetail(null)} />}
      {emailModal && <EmailModal email={emailModal} onClose={() => setEmailModal(null)} />}
      {editListing && <EditListingModal listingId={editListing} editForm={editForm} setEditForm={setEditForm} onClose={() => setEditListing(null)} onSaved={(id, form) => setListings(prev => prev.map(l => l.id === id ? { ...l, ...form } : l))} />}
    </div>
  );
}
